<div class="tab-content group-management">
  <!-- RBAC Teaser Banner for non-RBAC accounts -->
  @if (!rbacFullEnabled) {
    <div class="rbac-teaser-banner">
      <svg lucideLock></svg>
      <span>
        <strong>Unlock Group-Based Access Control</strong> — Organize users into groups for easier role management.
        Contact your account manager to enable this feature.
      </span>
    </div>
  }

  @if (rbacFullEnabled) {
    <div>Organize access by creating groups that combine roles and applications.</div>
  }
  <div class="flex justify-between items-center margin-top">
    <div class="flex items-center">
      <h2>All Groups</h2>
      @if (canManageGroups$ | async) {
        <button
          type="button"
          class="arpio-secondary-button small-button margin-left"
          (click)="openNewGroupModal()"
          data-cy="create-group-button"
        >
          <svg lucidePlus class="text-sm"></svg>Create New Group
        </button>
      }
    </div>
    <div>
      <div class="filter-container">
        <input
          pInputText
          class="input-small"
          #filterInput
          type="text"
          (input)="filterGlobal($event)"
          placeholder="Search..."
        />
        @if (loadingFilterInput) {
          <svg lucideLoaderCircle class="filter-spinner lucide-spin"></svg>
        }
        @if (!isFilterApplied && !loadingFilterInput) {
          <svg lucideFunnel class="filter-icon fade-icon"></svg>
        }
        @if (isFilterApplied && !loadingFilterInput) {
          <svg lucideX class="filter-clear-icon" (click)="clearInput()"></svg>
        }
      </div>
    </div>
  </div>
  <!-- Unauthorized State -->
  @if ((canViewGroups$ | async) === false) {
    <div class="unauthorized-container" data-cy="unauthorized-message">
      <div class="unauthorized-content">
        <h3>Access restricted</h3>
        <p>{{ unauthorizedMessage$ | async }}</p>
      </div>
    </div>
  }

  <!-- Loading State - show while data hasn't loaded yet -->
  @if (groups === null) {
    <div class="loading-container"><svg lucideLoaderCircle class="lucide-spin"></svg> Loading groups...</div>
  }

  <!-- Authorized Content - only show when groups array exists (even if empty) -->
  @if (groups !== null && (canViewGroups$ | async) !== false) {
    <div class="table-container">
      <!-- Empty State -->
      @if (groups.length === 0) {
        <div class="empty-state-container">
          <div class="empty-state-content">
            <h3>No groups have been created yet.</h3>
            <p>
              Groups allow you to assign permissions at scale by bundling roles with applications. Create your first
              group to streamline access management.
            </p>
            @if (canManageGroups$ | async) {
              <button class="arpio-button" (click)="openNewGroupModal()">
                <svg lucidePlus></svg>
                Create Your First Group
              </button>
            }
          </div>
        </div>
      }
      <!-- Table Section - Only show when groups exist -->
      @if (groups.length > 0) {
        <div class="table-section main-table">
          <p-table
            #dt1
            [value]="groups"
            data-cy="group-table"
            [globalFilterFields]="globalFilterFields"
            dataKey="id"
            [scrollable]="true"
            scrollHeight="60vh"
            [tableStyle]="{ 'min-width': '30rem' }"
            class="p-datatable-sm arp-prime-datatable"
            [rowHover]="true"
            [totalRecords]="groups?.length ?? 0"
            [paginator]="false"
            [rowTrackBy]="groupTrackBy"
            selectionMode="single"
            [(selection)]="selectedTableItem"
            (onRowSelect)="onRowSelect($event.data)"
          >
            <ng-template #header>
              <tr>
                <th pSortableColumn="name" class="strong-text name-column">
                  Name <p-sort-icon field="name"></p-sort-icon>
                </th>
                <th pSortableColumn="roles" class="strong-text roles-column">
                  Roles <p-sort-icon field="roles"></p-sort-icon>
                </th>
                <th pSortableColumn="assignedUsersCount" class="strong-text user-count-column">
                  # Assigned Users <p-sort-icon field="assignedUsersCount"></p-sort-icon>
                </th>
                <th pSortableColumn="lastModified" class="strong-text last-modified-column">
                  Last Modified <p-sort-icon field="lastModified"></p-sort-icon>
                </th>
              </tr>
            </ng-template>
            <ng-template #body let-group>
              <tr
                [pSelectableRow]="group"
                class="cursor-pointer"
                [ngClass]="{
                  'selected-row': showSidePanel && selectedGroup && selectedGroup.id === group.id,
                  'refreshing-row': isGroupRefreshing(group),
                }"
              >
                <td class="name-column" style="position: relative">
                  <div class="group-info">
                    <div class="rbac-table-display-container">
                      @if (isGroupRefreshing(group)) {
                        <svg lucideLoaderCircle class="half-margin-right lucide-spin"></svg>
                      }
                      <span>{{ group.name }}</span>
                    </div>
                  </div>
                </td>
                <td class="roles-column">
                  <div class="roles-list">
                    @if (isGroupRefreshing(group)) {
                      <svg lucideLoaderCircle class="half-margin-right lucide-spin"></svg>
                    }
                    @if (group.roles.length === 0) {
                      <span class="no-data-text">None</span>
                    }
                    @for (role of getSortedGroupRolesLimited(group, 2); track role) {
                      <a
                        [routerLink]="['/' + accountService.accountId + '/settings/role-management']"
                        [queryParams]="{ selectRole: role.name }"
                        class="role-tag-link"
                      >
                        <p-tag severity="info" class="role-tag clickable-tag fit-width" value="{{ role.name }}">
                        </p-tag>
                      </a>
                    }
                    @if (group.roles.length > 2) {
                      <span
                        class="more-count"
                        [pTooltip]="getAdditionalRolesTooltip(group)"
                        tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                        tooltipPosition="top"
                        [autoHide]="false"
                        [hideDelay]="200"
                        [escape]="false"
                        tooltipEvent="hover"
                      >
                        +{{ group.roles.length - 2 }} more
                      </span>
                    }
                  </div>
                </td>
                <td class="user-count-column">
                  <div class="user-count-display">
                    <span class="user-count-badge">{{ group.assignedUsersCount }}</span>
                  </div>
                </td>
                <td class="last-modified-column">
                  {{ getRelativeTimeDisplay(group.lastModified) }}
                </td>
              </tr>
            </ng-template>
            <ng-template #emptymessage>
              <tr>
                <td colspan="4" class="empty-state-cell text-center">
                  <div class="empty-state-content">
                    <p class="empty-state-message">No results found.</p>
                    <p class="empty-state-submessage">Try adjusting your search or filter criteria.</p>
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template #summary>
              <div class="flex align-items-center justify-content-between">
                <span
                  >Displaying {{ dt1.filteredValue?.length ?? dt1.value?.length }} of
                  {{ dt1.value?.length }} groups</span
                >
              </div>
            </ng-template>
          </p-table>
        </div>
      }
      <!-- Side Panel -->
      <div class="side-panel" [ngClass]="{ open: showSidePanel }" #sidePanel>
        <div class="side-panel-header">
          <div class="flex items-center">
            <button class="close-btn" (click)="closeSidePanel()">
              <svg lucideChevronRight></svg>
            </button>
            <h3>Group Details</h3>
          </div>
          <div>
            @if (canManageGroups$ | async) {
              <button class="edit-button" (click)="openEditGroupModal()">
                <svg lucideSquarePen></svg>
              </button>
            }
          </div>
        </div>
        <div class="side-panel-content">
          <!-- Basic Information Section -->
          <div class="detail-section">
            <div class="detail-item">
              <label>Name</label>
              @if (showSidePanel && selectedGroup) {
                <span>{{ selectedGroup.name }}</span>
              }
              @if (!showSidePanel || !selectedGroup) {
                <div class="detail-skeleton"></div>
              }
            </div>
            @if (selectedGroup?.externalId || !showSidePanel) {
              <div class="detail-item">
                <label>External ID</label>
                @if (showSidePanel && selectedGroup?.externalId) {
                  <span>{{ selectedGroup.externalId }}</span>
                }
                @if (showSidePanel && selectedGroup && !selectedGroup.externalId) {
                  <span class="text-muted">Not set</span>
                }
                @if (!showSidePanel || !selectedGroup) {
                  <div class="detail-skeleton"></div>
                }
              </div>
            }
          </div>
          <div class="detail-section">
            @if (showSidePanel && selectedGroup) {
              <h4>Assigned Roles ({{ selectedGroup.roles.length }})</h4>
            }
            @if (!showSidePanel || !selectedGroup) {
              <h4>Roles</h4>
            }
            <div class="roles-list detail-item">
              @if (showSidePanel && selectedGroup) {
                @for (role of getSortedGroupRoles(); track role) {
                  <a
                    [routerLink]="['/' + accountService.accountId + '/settings/role-management']"
                    [queryParams]="{ selectRole: role.name }"
                    class="role-tag-link"
                  >
                    <p-tag severity="info" class="role-tag clickable-tag fit-width" value="{{ role.name }}"> </p-tag>
                  </a>
                }
              }
              @if (!showSidePanel || !selectedGroup) {
                <div class="tag-skeleton" style="margin-right: 0.25rem"></div>
                <div class="tag-skeleton" style="margin-right: 0.25rem"></div>
              }
            </div>
            <!-- No roles message -->
            @if (showSidePanel && selectedGroup && selectedGroup.roles.length === 0) {
              <div class="detail-item">
                <span class="no-data-text">No roles assigned to this group</span>
              </div>
            }
          </div>
          <!-- Policies Section -->
          <div class="detail-section">
            <h4>Policies ({{ getGroupPolicies().length }})</h4>
            <div class="detail-item">
              @if (showSidePanel && selectedGroup && getGroupPolicies().length > 0) {
                <div class="policies-list">
                  @for (policy of getGroupPolicies(); track policy; let i = $index) {
                    <div class="policy-item">
                      <div class="policy-header half-half-margin-bottom">
                        <h5>Policy {{ i + 1 }}</h5>
                      </div>
                      <div class="policy-description text-sm text-muted margin-bottom">
                        @if (getPolicyDescriptionComponents(policy); as components) {
                          <p-tag
                            severity="info"
                            class="access-level-tag-display fit-width"
                            style="display: inline-flex; margin-right: 0.25rem"
                            value="{{ components.accessLevel }}"
                          >
                          </p-tag
                          >{{ components.prefix }}
                          @if (components.showTooltip) {
                            <span
                              [pTooltip]="components.tooltipText"
                              tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                              tooltipPosition="top"
                              [autoHide]="false"
                              [hideDelay]="200"
                              [escape]="false"
                              tooltipEvent="hover"
                              class="text-underline-dotted"
                              tabindex="0"
                              role="note"
                              >{{ components.applicationsText }}</span
                            >
                          }
                          @if (!components.showTooltip) {
                            <span>{{ components.applicationsText }}</span>
                          }
                          {{ components.suffix }}
                        }
                      </div>
                      @if (getInheritedFromText(policy)) {
                        <div class="policy-inheritance text-xs field-description">
                          {{ getInheritedFromText(policy) }}
                        </div>
                      }
                    </div>
                  }
                </div>
              }
              @if (showSidePanel && selectedGroup && getGroupPolicies().length === 0) {
                <div class="no-data-text">No policies defined</div>
              }
              @if (!showSidePanel || !selectedGroup) {
                <div class="detail-skeleton"></div>
              }
            </div>
          </div>
          <!-- Assigned Users Section -->
          <div class="detail-section">
            @if (showSidePanel && selectedGroup) {
              <h4>Assigned Users ({{ selectedGroup.assignedUsersCount }})</h4>
            }
            @if (!showSidePanel || !selectedGroup) {
              <h4>Assigned Users</h4>
            }
            <!-- Search bar for assigned users -->
            @if (showSidePanel && selectedGroup && selectedGroup.assignedUsers.length > 10) {
              <div class="users-search-container margin-top">
                <input
                  pInputText
                  type="text"
                  #usersSearchInput
                  (input)="filterAssignedUsers($event)"
                  placeholder="Search users..."
                  class="arpio-input users-search"
                />
                <svg lucideSearch class="search-icon"></svg>
              </div>
            }
            <div class="users-list">
              @if (showSidePanel && selectedGroup) {
                @for (user of getFilteredAssignedUsers(); track user) {
                  <a
                    [routerLink]="['/' + accountService.accountId + '/settings/user-management']"
                    [queryParams]="{ selectUser: user.accountSubjectId }"
                    class="user-item clickable-user"
                    [class.sso-user]="isUserSsoAssigned(user)"
                  >
                    @if (!isUserSsoAssigned(user)) {
                      <svg lucideUser></svg>
                    }
                    @if (isUserSsoAssigned(user)) {
                      <svg lucideIdCard class="sso-icon"></svg>
                    }
                    <span>{{ user.name }}</span>
                  </a>
                }
                <!-- No users message -->
                @if (selectedGroup.assignedUsers && selectedGroup.assignedUsers.length === 0) {
                  <div class="detail-item">
                    <span class="no-data-text">No users assigned to this group</span>
                  </div>
                }
                <!-- No search results message -->
                @if (
                  selectedGroup.assignedUsers &&
                  selectedGroup.assignedUsers.length > 0 &&
                  getFilteredAssignedUsers().length === 0 &&
                  usersSearchText
                ) {
                  <div class="no-data-text">No users found matching "{{ usersSearchText }}"</div>
                }
              }
              @if (!showSidePanel || !selectedGroup) {
                <div class="user-item">
                  <svg lucideUser></svg>
                  <div class="detail-skeleton" style="width: 200px"></div>
                </div>
                <div class="user-item">
                  <svg lucideUser></svg>
                  <div class="detail-skeleton" style="width: 180px"></div>
                </div>
                <div class="user-item">
                  <svg lucideUser></svg>
                  <div class="detail-skeleton" style="width: 220px"></div>
                </div>
              }
            </div>
          </div>
          <!-- Additional Information Section -->
          <div class="detail-section">
            <h4>Additional Information</h4>
            <div class="detail-item">
              <label>Created</label>
              @if (showSidePanel && selectedGroup) {
                <span>{{ selectedGroup.createdAt | date: 'medium' }}</span>
              }
              @if (!showSidePanel || !selectedGroup) {
                <div class="detail-skeleton"></div>
              }
            </div>
            <div class="detail-item">
              <label>Last Modified</label>
              @if (showSidePanel && selectedGroup) {
                <span>{{ selectedGroup.lastModified | date: 'medium' }}</span>
              }
              @if (!showSidePanel || !selectedGroup) {
                <div class="detail-skeleton"></div>
              }
            </div>
          </div>
          <!-- Action Buttons Section -->
          @if (showSidePanel && selectedGroup && (canManageGroups$ | async)) {
            <div class="detail-section action-buttons">
              <h4>Actions</h4>
              <div class="margin-auto text-center margin-top">
                <a class="delete-group-button text-center" (click)="deleteGroup()">Delete Group...</a>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  }

  <ng-template #loadingBlock> <svg lucideLoaderCircle class="lucide-spin"></svg> Loading groups... </ng-template>

  <app-new-group-modal #newGroupModal (groupCreated)="onGroupCreated()"></app-new-group-modal>
  <app-edit-group-modal #editGroupModal (groupUpdated)="onGroupUpdated($event)"></app-edit-group-modal>

  <!-- Delete Group Confirmation Modal -->
  <modal-dialog #deleteGroupDialog [dialogTemplate]="confirmDeleteGroupDialog"></modal-dialog>
  <ng-template #confirmDeleteGroupDialog>
    <confirm-action-dialog
      name="&quot;{{ groupToDelete?.name }}&quot;"
      action="Delete"
      dialogId="delete-group-confirm"
      (onCancel)="cancelDeleteGroup()"
      (onAction)="confirmDeleteGroup()"
    >
      <div class="error margin-top">
        Deleting this group will remove it from all assigned users. This action cannot be undone.
      </div>
    </confirm-action-dialog>
  </ng-template>
</div>
